import useResident from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import type { CasePeople } from '../../steps/PeopleInvolvedStep'
import { useServiceUserWords } from '../../useServiceUserWords'
import { ReadItem, ReadWide } from '../readLayout'
import { serviceUserName, toEntryNames, toNames } from '../readRules'
import { useServiceUserName, useTeamMemberName } from '../usePersonName'

const testId = 'incident-case-v2-view-peopleInvolved'

/**
 * Step 3, read only: the case's own service user (with the name staff use, as
 * on the details card), then other service users, team members, parties and
 * witnesses.
 */
const PeopleInvolvedReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { plural, singular } = useServiceUserWords()
  const people = (incidentReport.case ?? {}) as CasePeople
  const { serviceUser } = incidentReport
  // Shares the details card's query; the read embeds no preferred name.
  const { resident } = useResident({ id: serviceUser.id })
  const serviceUserNames = useServiceUserName()
  const teamMemberNames = useTeamMemberName()

  return (
    <>
      <ReadWide>
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_VIEW_PEOPLE_SERVICE_USER, {
            variable: singular
          })}
          testId={`${testId}-service-user`}
          value={serviceUserName(t, serviceUser.name, resident?.preferredName)}
        />
      </ReadWide>
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_PEOPLE_OTHER_SERVICE_USERS, {
          variable: plural
        })}
        isLoading={serviceUserNames.isLoading}
        testId={`${testId}-service-users`}
        value={toNames(people.serviceUsersInvolved, serviceUserNames.nameOf)}
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_PEOPLE_TEAM_MEMBERS)}
        isLoading={teamMemberNames.isLoading}
        testId={`${testId}-team-members`}
        value={toNames(people.carersInvolved, teamMemberNames.nameOf)}
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_PEOPLE_OTHER_PARTIES)}
        testId={`${testId}-other-parties`}
        value={toEntryNames(
          people.otherPartiesInvolved,
          people.otherPeopleInvolved
        )}
      />
      <ReadItem
        label={t(incidentReportsConstants.CASE_V2_PEOPLE_WITNESSES)}
        testId={`${testId}-witnesses`}
        value={toEntryNames(people.witnesses, people.otherWitnesses)}
      />
    </>
  )
}

export default PeopleInvolvedReadPanel
